import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Beaker } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import { RDCalculator } from "@/pages/RDTaxCredit";
import useSEO from "@/lib/useSEO";

export default function RDCalculatorPage() {
  useSEO({
    title: "R&D Tax Credit Calculator | Estimate Your Federal Credit | IncentTax",
    description:
      "Free R&D tax credit calculator. Enter your qualified research expenses (QREs) — wages, supplies, contract research — to estimate your federal and state R&D credit instantly.",
    canonical: "https://incenttax.com/calculators/rd-tax-credit",
  });

  return (
    <PageShell testid="rd-calculator-page">
      <section className="bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-24">
          <p className="text-brass uppercase tracking-[0.28em] text-sm mb-6">Free Calculator</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
            R&D Tax Credit
            <br />
            <span className="italic text-brass">Estimator.</span>
          </h1>
          <p className="mt-6 text-ivory/85 text-lg sm:text-xl leading-relaxed max-w-3xl">
            Plug in your wages, supplies, contract research, and prior-year gross receipts
            to estimate your federal R&D credit (plus state credit, where applicable) — based
            on the IRS Alternative Simplified Credit (ASC) and Startup methods.
          </p>
        </div>
      </section>

      <RDCalculator />

      <section className="py-20 bg-forest-900 text-ivory">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <Beaker className="text-brass mx-auto mb-6" size={36} strokeWidth={1.5} />
          <h2 className="font-serif text-3xl sm:text-4xl text-ivory leading-tight mb-6">
            Ready to claim it? <span className="italic text-brass">We'll handle the rest.</span>
          </h2>
          <p className="text-ivory/80 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            We deliver the federal credit calculation, state credit (where applicable),
            and audit-ready technical narratives — your CPA files, you collect the cash.
          </p>
          <Link
            to="/#contact"
            data-testid="rd-calc-bottom-cta"
            className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-10 py-4 font-medium"
          >
            Request a free technical review <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
